import { ChevronDown } from '@insify/design-system/icons';
import clsx from 'clsx';
import { useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import * as styles from './InputSelect.module.css';

import type React from 'react';
import type { ComponentType, ReactNode } from 'react';
import type { FieldValues, RegisterOptions, UseControllerProps } from 'react-hook-form';

/**
 * @deprecated
 */
export type InputSelectProps<Value> = {
  value: Value;
  id: string;
  className?: string;
  placeholder?: string;
  before?: string;
  children?: React.ReactNode;
  onUpdate?: (value: Value) => void;
  onChange: (value: Value) => void;
  options: {
    label: string | ReactNode;
    value: Value;
  }[];
  Component?: ComponentType<React.ComponentPropsWithRef<'select'>> | 'select';
  error?: string;
};

/**
 * @deprecated
 */
export type InputSelectPropsController<ValueType, FieldType extends FieldValues> = Omit<
  InputSelectProps<ValueType>,
  'value' | 'onChange' | 'id'
> &
  UseControllerProps<FieldType> & {
    rules?: Exclude<RegisterOptions, 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>;
    onChange?: (value: ValueType) => void;
  };

/**
 * @deprecated
 */
const Select = <Value,>({
  placeholder = '',
  className = '',
  onUpdate,
  error,
  Component = 'select',
  ...props
}: InputSelectProps<Value>): JSX.Element => {
  const valueIndex = useMemo((): number | string => {
    const indexValue = props.value ? String(props.value) : '';
    const index = props.options.map((option) => String(option.value)).indexOf(indexValue);
    return index < 0 ? '' : index;
  }, [props.value, props.options]);

  function handleChange(e: React.ChangeEvent<HTMLSelectElement>): void {
    const value = props.options[Number(e.target.value)].value;
    value && props.onChange(value);
  }

  useEffect(() => {
    props.value && onUpdate && onUpdate(props.value);
  }, [props.value]);

  const isEmpty = props.value === undefined;
  const valueIndexAsNumber = typeof valueIndex === 'number' ? valueIndex : 0;
  const isShowingPlaceholder = valueIndexAsNumber < 0;

  return (
    <div className={clsx(styles.field, error && styles.error, className)}>
      {props.children && (
        <label htmlFor={props.id} className={styles.fieldLabel}>
          {props.children}
        </label>
      )}
      <Component
        onChange={handleChange}
        value={valueIndex}
        className={clsx(styles.fieldInput, isShowingPlaceholder && styles.placeholder, isEmpty && styles.empty)}
        id={props.id}
      >
        {placeholder && (
          <option value={''} label={`${props.before ? props.before + ' ' : ''}${placeholder}`} disabled />
        )}
        {props.options.map((option, index) => {
          const key = `${index}-${props.id}`;
          return <option key={key} value={index}>{`${props.before ? props.before + ' ' : ''}${option.label}`}</option>;
        })}
      </Component>
      {error && <span>{error}</span>}
      <ChevronDown className={styles.arrowIcon} color='#000000' />
    </div>
  );
};

/**
 * @deprecated
 * Select input with variable value. <InputSelect.Wrapper> can combine multiple InputSelect components
 */
export const InputSelect = <ValueType, FieldType extends FieldValues>({
  name,
  control,
  defaultValue = undefined,
  rules,
  onChange,
  Component = 'select',
  ...selectProps
}: InputSelectPropsController<ValueType, FieldType>): JSX.Element => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field }): JSX.Element => {
        function handleChange(value: ValueType): void {
          onChange && onChange(value);
          field.onChange(value);
        }
        return (
          <Select<ValueType>
            {...selectProps}
            Component={Component}
            value={field.value}
            id={field.name}
            className={selectProps.className || ''}
            onChange={handleChange}
          />
        );
      }}
    />
  );
};
